/**
 * RightSidebar - Sticky right sidebar with Messages, My Events, and My Notifications sections (divided into thirds).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useSocket } from '../../utils/SocketContext';
import { getConversations } from '../../helpers/messagesHelpers';
import { getEvents } from '../../helpers/eventsHelpers';
import {
  getUnreadNotifications,
  deleteNotification,
  acceptClubInvite,
  declineClubInvite,
  acceptEventInvite,
  declineEventInvite,
  acceptFollowRequest,
  declineFollowRequest,
  getNotificationLabel,
  getNotificationLink,
} from '../../helpers/notificationsHelpers';
import { useNotificationCount } from '../../utils/NotificationCountContext';
import { acceptClubJoinRequest, declineClubJoinRequest } from '../../helpers/clubsHelpers';
import UserAvatar from '../Profile/UserAvatar';
import CreateEventModal from '../../modals/CreateEventModal';
import toast from 'react-hot-toast';

const RECENT_LIMIT = 3;

function formatEventDate(d) {
  const dt = new Date(d);
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function sidebarClubJoinKey(n) {
  return n.club_id != null && n.actor_id != null ? `${n.club_id}-${n.actor_id}` : null;
}

function RightSidebar() {
  const [conversations, setConversations] = useState([]);
  const [events, setEvents] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [actingInviteId, setActingInviteId] = useState(null);
  const [actingFollowerId, setActingFollowerId] = useState(null);
  const [actingClubJoinKey, setActingClubJoinKey] = useState(null);
  const [deletingNotificationId, setDeletingNotificationId] = useState(null);
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const { subscribe, connected } = useSocket();
  const { refreshUnreadCount } = useNotificationCount();

  const loadUnreadNotifications = useCallback(() => {
    setNotificationsLoading(true);
    getUnreadNotifications(RECENT_LIMIT)
      .then((list) => setUnreadNotifications(Array.isArray(list) ? list : []))
      .catch(() => setUnreadNotifications([]))
      .finally(() => setNotificationsLoading(false));
  }, []);

  useEffect(() => {
    loadUnreadNotifications();
  }, [loadUnreadNotifications]);

  useEffect(() => {
    if (!connected) return;
    return subscribe('notification', () => {
      loadUnreadNotifications();
    });
  }, [connected, subscribe, loadUnreadNotifications]);

  const fetchEvents = () => {
    getEvents(true)
      .then((data) => setEvents(Array.isArray(data) ? data : []))
      .catch(() => setEvents([]))
      .finally(() => setEventsLoading(false));
  };

  useEffect(() => {
    setEventsLoading(true);
    fetchEvents();
  }, []);

  useEffect(() => {
    let cancelled = false;
    getConversations(true)
      .then((data) => {
        if (!cancelled) setConversations(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setConversations([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleAcceptInvite = async (n) => {
    if (actingInviteId || !n.invite_id) return;
    setActingInviteId(n.invite_id);
    try {
      if (n.notification_type_id === 'event_invite') {
        await acceptEventInvite(n.invite_id);
        toast.success(`You're going to ${n.event_name || 'the event'}`);
      } else {
        await acceptClubInvite(n.invite_id);
        toast.success(`Joined ${n.club_name}`);
      }
      setUnreadNotifications((prev) => prev.filter((x) => x.notification_id !== n.notification_id));
      refreshUnreadCount();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActingInviteId(null);
    }
  };

  const handleDeclineInvite = async (n) => {
    if (actingInviteId || !n.invite_id) return;
    setActingInviteId(n.invite_id);
    try {
      if (n.notification_type_id === 'event_invite') {
        await declineEventInvite(n.invite_id);
      } else {
        await declineClubInvite(n.invite_id);
      }
      setUnreadNotifications((prev) => prev.filter((x) => x.notification_id !== n.notification_id));
      refreshUnreadCount();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActingInviteId(null);
    }
  };

  const handleAcceptFollowRequest = async (n) => {
    if (actingFollowerId || !n.follower_id) return;
    setActingFollowerId(n.follower_id);
    try {
      await acceptFollowRequest(n.follower_id);
      toast.success(`Accepted ${n.actor_displayname || n.actor_username}`);
      setUnreadNotifications((prev) => prev.filter((x) => x.notification_id !== n.notification_id));
      refreshUnreadCount();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActingFollowerId(null);
    }
  };

  const handleDeclineFollowRequest = async (n) => {
    if (actingFollowerId || !n.follower_id) return;
    setActingFollowerId(n.follower_id);
    try {
      await declineFollowRequest(n.follower_id);
      setUnreadNotifications((prev) => prev.filter((x) => x.notification_id !== n.notification_id));
      refreshUnreadCount();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActingFollowerId(null);
    }
  };

  const handleAcceptClubJoinRequest = async (n) => {
    const key = sidebarClubJoinKey(n);
    if (actingClubJoinKey || !key || n.club_id == null) return;
    setActingClubJoinKey(key);
    try {
      await acceptClubJoinRequest(n.club_id, n.actor_id);
      toast.success(`Accepted ${n.actor_displayname || n.actor_username}`);
      setUnreadNotifications((prev) => prev.filter((x) => x.notification_id !== n.notification_id));
      refreshUnreadCount();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActingClubJoinKey(null);
    }
  };

  const handleDeclineClubJoinRequest = async (n) => {
    const key = sidebarClubJoinKey(n);
    if (actingClubJoinKey || !key || n.club_id == null) return;
    setActingClubJoinKey(key);
    try {
      await declineClubJoinRequest(n.club_id, n.actor_id);
      setUnreadNotifications((prev) => prev.filter((x) => x.notification_id !== n.notification_id));
      refreshUnreadCount();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActingClubJoinKey(null);
    }
  };

  const handleRemoveNotification = async (n) => {
    if (deletingNotificationId != null) return;
    setDeletingNotificationId(n.notification_id);
    try {
      await deleteNotification(n.notification_id);
      setUnreadNotifications((prev) => prev.filter((x) => x.notification_id !== n.notification_id));
      refreshUnreadCount();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeletingNotificationId(null);
    }
  };

  const recentConversations = conversations.slice(0, RECENT_LIMIT);
  const recentEvents = events.slice(0, RECENT_LIMIT);

  return (
    <aside
      className="hidden lg:block w-1/5 flex-shrink-0 min-h-0 overflow-y-auto border-l border-shadow bg-charcoal/50"
      data-testid="right-sidebar"
    >
      <div className="p-4 flex flex-col gap-6">
        <section className="flex-1 min-h-0 flex flex-col">
          <h2 className="text-ash font-medium text-sm mb-2 flex-shrink-0">My Messages</h2>
          {loading ? (
            <p className="text-silver text-xs">Loading...</p>
          ) : recentConversations.length === 0 ? (
            <p className="text-silver text-xs">Aura waits within every new connection.</p>
          ) : (
            <ul className="space-y-1">
              {recentConversations.map((c) => {
                const isClub = c.type === 'club';
                const isGroup = c.type === 'group';
                const displayName = isClub
                  ? c.club_name
                  : isGroup
                    ? (c.display_name || 'Group')
                    : (c.other_user?.displayname || c.other_user?.username || 'Unknown');
                const to = isClub
                  ? `/clubs/${c.club_id}?tab=chat`
                  : isGroup
                    ? `/messages/group/${c.group_conversation_id}`
                    : `/messages/${c.conversation_id}`;
                const otherId = c.other_user?.user_id;
                const showYouPrefix = !isClub && !isGroup && c.last_message_sender_id !== otherId;
                const key = isClub ? `club-${c.club_id}` : isGroup ? `group-${c.group_conversation_id}` : c.conversation_id;
                const avatarUser = isClub
                  ? {
                      user_id: c.club_id,
                      username: c.club_name,
                      displayname: displayName,
                      profile_pic_url: c.club_avatar_url,
                    }
                  : isGroup
                    ? {
                        user_id: c.group_conversation_id,
                        username: c.display_name,
                        displayname: displayName,
                        profile_pic_url: c.avatar_url,
                        default_profile_color: c.default_profile_color,
                      }
                    : c.other_user;
                return (
                  <li key={key}>
                    <Link
                      to={to}
                      className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-shadow"
                    >
                      <UserAvatar
                        user={avatarUser}
                        className={`w-8 h-8 flex-shrink-0 ${isClub ? 'rounded-lg' : 'rounded-full'}`}
                        emojiClassName="text-xs"
                        alt={displayName}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-ash text-sm truncate">{displayName}</p>
                        <p className="text-silver text-xs truncate">
                          {showYouPrefix ? 'You: ' : ''}
                          {c.last_message_preview || 'No messages yet'}
                        </p>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
          <Link
            to="/messages"
            className="block py-1.5 px-2 rounded-lg text-ash text-sm hover:bg-shadow mt-2"
          >
            + New Conversation
          </Link>
          <Link
            to="/messages"
            className="mt-2 block text-center py-2 text-ether text-sm font-medium hover:underline rounded-lg"
          >
            View All Messages
          </Link>
        </section>

        <section className="flex-1 min-h-0 flex flex-col">
          <h2 className="text-ash font-medium text-sm mb-2 flex-shrink-0">My Events</h2>
          {eventsLoading ? (
            <p className="text-silver text-xs">Loading...</p>
          ) : recentEvents.length === 0 ? (
            <p className="text-silver text-xs">Nothing on the horizon. Presence doesn&apos;t need plans.</p>
          ) : (
            <ul className="space-y-1 min-h-0 overflow-y-auto">
              {recentEvents.map((e) => (
                <li key={e.event_id}>
                  <Link
                    to={`/events/${e.event_id}`}
                    className="block py-1.5 px-2 rounded-lg text-ash text-sm hover:bg-shadow truncate"
                  >
                    {e.name}
                  </Link>
                  <p className="text-silver text-xs px-2">{formatEventDate(e.start_time)}</p>
                </li>
              ))}
            </ul>
          )}
          <button
            type="button"
            onClick={() => setShowCreateEventModal(true)}
            className="block py-1.5 px-2 rounded-lg text-ash text-sm hover:bg-shadow mt-2 w-full text-left flex-shrink-0"
          >
            + New Event
          </button>
          <Link
            to="/events"
            className="mt-2 block text-center py-2 text-ether text-sm font-medium hover:underline rounded-lg flex-shrink-0"
          >
            View All Events
          </Link>
        </section>

        <section className="flex-1 min-h-0 flex flex-col">
          <h2 className="text-ash font-medium text-sm mb-2 flex-shrink-0">My Notifications</h2>
          {notificationsLoading ? (
            <p className="text-silver text-xs">Loading...</p>
          ) : unreadNotifications.length === 0 ? (
            <p className="text-silver text-xs">Life either comes at you or from you. Send aura to receive aura.</p>
          ) : (
            <ul className="space-y-1">
              {unreadNotifications.map((n) => {
                const link = getNotificationLink(n);
                const followRequestPending =
                  n.notification_type_id === 'follow_request' && n.follow_request_pending === true;
                const cjKey = sidebarClubJoinKey(n);
                return (
                  <li key={n.notification_id} className="py-1.5 px-2 rounded-lg hover:bg-shadow">
                    {link ? (
                      <Link to={link} className="block">
                        <p className="text-ash text-sm truncate">{getNotificationLabel(n)}</p>
                      </Link>
                    ) : (
                      <p className="text-ash text-sm truncate">{getNotificationLabel(n)}</p>
                    )}
                    <div className="flex gap-2 mt-1 flex-wrap items-center">
                      {followRequestPending && (
                        <>
                          <button
                            type="button"
                            onClick={() => handleAcceptFollowRequest(n)}
                            disabled={actingFollowerId !== null}
                            className="text-ether text-xs font-medium hover:underline disabled:opacity-50"
                          >
                            {actingFollowerId === n.follower_id ? '...' : 'Accept'}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeclineFollowRequest(n)}
                            disabled={actingFollowerId !== null}
                            className="text-silver text-xs hover:text-ash disabled:opacity-50"
                          >
                            Decline
                          </button>
                        </>
                      )}
                      {n.notification_type_id === 'club_invite' && (
                        <>
                          <button
                            type="button"
                            onClick={() => handleAcceptInvite(n)}
                            disabled={actingInviteId !== null}
                            className="text-ether text-xs font-medium hover:underline disabled:opacity-50"
                          >
                            {actingInviteId === n.invite_id ? '...' : 'Accept'}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeclineInvite(n)}
                            disabled={actingInviteId !== null}
                            className="text-silver text-xs hover:text-ash disabled:opacity-50"
                          >
                            Decline
                          </button>
                        </>
                      )}
                      {n.notification_type_id === 'event_invite' && (
                        <>
                          <button
                            type="button"
                            onClick={() => handleAcceptInvite(n)}
                            disabled={actingInviteId !== null}
                            className="text-ether text-xs font-medium hover:underline disabled:opacity-50"
                          >
                            {actingInviteId === n.invite_id ? '...' : 'Accept'}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeclineInvite(n)}
                            disabled={actingInviteId !== null}
                            className="text-silver text-xs hover:text-ash disabled:opacity-50"
                          >
                            Decline
                          </button>
                        </>
                      )}
                      {n.notification_type_id === 'club_join_request' && (
                        <>
                          <button
                            type="button"
                            onClick={() => handleAcceptClubJoinRequest(n)}
                            disabled={actingClubJoinKey !== null}
                            className="text-ether text-xs font-medium hover:underline disabled:opacity-50"
                          >
                            {actingClubJoinKey === cjKey ? '...' : 'Accept'}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeclineClubJoinRequest(n)}
                            disabled={actingClubJoinKey !== null}
                            className="text-silver text-xs hover:text-ash disabled:opacity-50"
                          >
                            Decline
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemoveNotification(n)}
                        disabled={deletingNotificationId !== null}
                        className="text-silver text-xs hover:text-ash disabled:opacity-50 ml-auto"
                      >
                        {deletingNotificationId === n.notification_id ? '...' : 'Remove'}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <Link
            to="/notifications"
            className="mt-2 block text-center py-2 text-ether text-sm font-medium hover:underline rounded-lg flex-shrink-0"
          >
            View All Notifications
          </Link>
        </section>
      </div>
      {showCreateEventModal && (
        <CreateEventModal
          onClose={() => setShowCreateEventModal(false)}
          onSuccess={fetchEvents}
        />
      )}
    </aside>
  );
}

export default RightSidebar;
